import { addDays, differenceInCalendarDays, format, subSeconds } from 'date-fns';
import { toast } from 'react-toastify';

import { createDefaultHeaders } from '../api';
import { getSession, urlWithAdminQuery } from '../auth';
import type {
  DeviceHourlyUsageObject,
  DeviceUsageObject,
  UnitHourlyUsage,
  UnitUsage,
} from './types';

// API Calls //////////////////////////////////////////////////////////////////

// Get ////////////////////////////////

/**
 * Get usage from backend
 *
 * @param  {string} unitId - ID for specififc unit to get usage for
 * @param  {number} start - Unix timestamp for start of date range
 * @param  {number} end - Unix timestamp for end of date range
 * @returns {Promise} - Result of call to get usage
 */
export const get = (
  unitId: string,
  start: Date,
  end: Date,
  isElasticsearchOutage: boolean,
  timezone_key: string,
): Promise<UnitUsage> => {
  if (isElasticsearchOutage) {
    const outageError = new Error(`Under maintenance! The usage system is currently unavailable.`);
    toast.error(outageError.message, { closeButton: true });
    return Promise.reject(outageError);
  }

  const MAX_DAYS = Number(process.env.REACT_APP_USAGE_CHART_DATEPICKER_MAX_DAYS_IN_PAST) || 365;

  const diffInDays = differenceInCalendarDays(end, start);
  const numberOfRanges = Math.ceil(diffInDays / MAX_DAYS);
  const rangeArr = Array(numberOfRanges)
    .fill({})
    .map((_, idx, arr) => {
      const newStart = addDays(start, MAX_DAYS * idx).getTime();
      const newEnd =
        idx === arr.length - 1
          ? end.getTime()
          : subSeconds(addDays(newStart, MAX_DAYS), 1).getTime();
      const dateStart = new Date(newStart);
      const dateEnd = new Date(newEnd);
      return {
        start: format(dateStart, 'yyyy-MM-dd'),
        end: format(dateEnd, 'yyyy-MM-dd'),
      };
    });
  const tZone =
    timezone_key === '' ? Intl.DateTimeFormat().resolvedOptions().timeZone : timezone_key;

  return getSession().then((session) =>
    Promise.all(
      rangeArr.map((range) =>
        fetch(
          urlWithAdminQuery(
            `/unit/${unitId}/usage?start_date=${range.start}&end_date=${
              range.end
            }&timezone_key=${encodeURIComponent(tZone)}`,
            session.adminEmail,
          ),
          {
            method: 'GET',
            headers: createDefaultHeaders({
              authToken: session.idToken.toString(),
            }),
          },
        )
          .then(async (resp) => {
            if (!resp.ok) {
              // Handle permission errors - return rejected promise
              if (resp.status === 403) {
                const errorText = await resp.text();
                return Promise.reject(new Error(`PERMISSION_DENIED: ${errorText}`));
              }
              return Promise.reject(new Error(`HTTP ${resp.status}: ${resp.statusText}`));
            }
            return resp.json();
          })
          .catch((err) => {
            console.error(err);
            // Only show toast for non-permission errors
            if (!err.message?.includes('PERMISSION_DENIED')) {
              toast.error(
                "We couldn't load usage data for this unit. Please try again, and contact support if the problem continues.",
                { closeButton: true },
              );
            }
            // Re-throw permission errors so UI can handle them
            return Promise.reject(err);
          }),
      ),
    ).then((resp) => {
      const devices: DeviceUsageObject[] = [];

      resp.forEach((unit) => {
        unit.devices.forEach((device) => {
          const existingDevice = devices.find((d) => d.id === device.id);

          if (existingDevice) {
            existingDevice.daily_usages = [...existingDevice.daily_usages];
          } else {
            devices.push(device);
          }
        });
      });
      return {
        ...resp[0],
        devices,
      };
    }),
  );
};

/**
 * Get hourly usage from backend. Privacy-gated: requires VIEW_USAGE permission AND the
 * per-membership HOURLY_USAGE feature flag (granted by Customer Support). Same range-chunking
 * behavior as the daily `get` above.
 */
export const getHourly = (
  unitId: string,
  start: Date,
  end: Date,
  isElasticsearchOutage: boolean,
  timezone_key: string,
): Promise<UnitHourlyUsage> => {
  if (isElasticsearchOutage) {
    const outageError = new Error(`Under maintenance! The usage system is currently unavailable.`);
    toast.error(outageError.message, { closeButton: true });
    return Promise.reject(outageError);
  }

  const MAX_DAYS = Number(process.env.REACT_APP_USAGE_CHART_DATEPICKER_MAX_DAYS_IN_PAST) || 365;

  const diffInDays = differenceInCalendarDays(end, start);
  const numberOfRanges = Math.ceil(diffInDays / MAX_DAYS);
  const rangeArr = Array(numberOfRanges)
    .fill({})
    .map((_, idx, arr) => {
      const newStart = addDays(start, MAX_DAYS * idx).getTime();
      const newEnd =
        idx === arr.length - 1
          ? end.getTime()
          : subSeconds(addDays(newStart, MAX_DAYS), 1).getTime();
      const dateStart = new Date(newStart);
      const dateEnd = new Date(newEnd);
      return {
        start: format(dateStart, 'yyyy-MM-dd'),
        end: format(dateEnd, 'yyyy-MM-dd'),
      };
    });
  const tZone =
    timezone_key === '' ? Intl.DateTimeFormat().resolvedOptions().timeZone : timezone_key;

  return getSession().then((session) =>
    Promise.all(
      rangeArr.map((range) =>
        fetch(
          urlWithAdminQuery(
            `/unit/${unitId}/usage/hourly?start_date=${range.start}&end_date=${
              range.end
            }&timezone_key=${encodeURIComponent(tZone)}`,
            session.adminEmail,
          ),
          {
            method: 'GET',
            headers: createDefaultHeaders({
              authToken: session.idToken.toString(),
            }),
          },
        )
          .then(async (resp) => {
            if (!resp.ok) {
              if (resp.status === 403) {
                const errorText = await resp.text();
                return Promise.reject(new Error(`PERMISSION_DENIED: ${errorText}`));
              }
              return Promise.reject(new Error(`HTTP ${resp.status}: ${resp.statusText}`));
            }
            return resp.json();
          })
          .catch((err) => {
            console.error(err);
            if (!err.message?.includes('PERMISSION_DENIED')) {
              toast.error(
                "We couldn't load hourly usage data for this unit. Please try again, and contact support if the problem continues.",
                { closeButton: true },
              );
            }
            return Promise.reject(err);
          }),
      ),
    ).then((resp) => {
      const devices: DeviceHourlyUsageObject[] = [];

      resp.forEach((unit) => {
        unit.devices.forEach((device) => {
          const existingDevice = devices.find((d) => d.id === device.id);

          if (existingDevice) {
            existingDevice.hourly_usages = [
              ...existingDevice.hourly_usages,
              ...device.hourly_usages,
            ];
          } else {
            devices.push(device);
          }
        });
      });
      return {
        ...resp[0],
        devices,
      };
    }),
  );
};
